import { useState } from "react";
import { X, PhoneCall, Video, ArrowRight, CheckCircle2 } from "lucide-react";

const MEETING_OPTIONS = [
    {
        key: "call",
        label: "Direct Call",
        description: "Connect over a regular voice call",
        icon: PhoneCall,
    },
    {
        key: "video",
        label: "Video Conference",
        description: "Join a face-to-face video session",
        icon: Video,
    },
];

export default function JoinMeetingModal({ booking, onClose, onJoined }) {
    const [selected, setSelected] = useState(null);
    const [joined, setJoined] = useState(false);

    if (!booking) return null;

    const handleJoin = () => {
        if (!selected) return;
        // Wire this up to your real call/video integration.
        // e.g. window.open(videoRoomUrl) or start a telephony session.
        onJoined?.(booking.id, selected);
        setJoined(true);
    };

    const handleClose = () => {
        setSelected(null);
        setJoined(false);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={handleClose}
        >
            <div
                className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white font-semibold">
                            {booking.initials}
                        </div>
                        <div>
                            <p className="font-semibold text-slate-900 leading-tight">{booking.mentorName}</p>
                            <p className="text-sm text-slate-500">{booking.course}</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="Close"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {joined ? (
                    <div className="p-6 text-center space-y-2">
                        <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" />
                        <p className="font-semibold text-slate-900">
                            Joining via {MEETING_OPTIONS.find((o) => o.key === selected)?.label}
                        </p>
                        <p className="text-sm text-slate-500">
                            {booking.mentorName} · {booking.time}
                        </p>
                        <button
                            onClick={handleClose}
                            className="mt-3 w-full rounded-lg bg-slate-900 text-white py-2 text-sm font-medium hover:bg-slate-800"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="p-5 space-y-4">
                        <p className="text-xs font-medium text-slate-500">How would you like to join?</p>

                        <div className="space-y-2">
                            {MEETING_OPTIONS.map((opt) => {
                                const Icon = opt.icon;
                                const isSelected = selected === opt.key;
                                return (
                                    <button
                                        key={opt.key}
                                        onClick={() => setSelected(opt.key)}
                                        className={`w-full flex items-center gap-3 rounded-xl border p-3.5 text-left transition-colors ${isSelected
                                            ? "border-indigo-600 bg-indigo-50"
                                            : "border-slate-200 hover:border-indigo-300"
                                            }`}
                                    >
                                        <div
                                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isSelected ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"
                                                }`}
                                        >
                                            <Icon className="h-5 w-5" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-semibold text-slate-900">{opt.label}</p>
                                            <p className="text-xs text-slate-500">{opt.description}</p>
                                        </div>
                                        <div
                                            className={`h-4 w-4 shrink-0 rounded-full border-2 ${isSelected ? "border-indigo-600 bg-indigo-600" : "border-slate-300"
                                                }`}
                                        />
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            disabled={!selected}
                            onClick={handleJoin}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white py-2.5 text-sm font-medium shadow-sm hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                        >
                            Join now
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}